import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { NgbActiveModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { PtCoreActions, PtCoreSelectors } from '@pt/pt-core';
import {
  DepartmentService,
  FolderService,
  LegalEntityService,
  SystemService,
  TreatmentCategoryService,
  TreatmentService,
  UsersService,
} from '@pt/api-client';
import { TreatmentActions, ValidatorGenerator } from '@pt/pt-shared';
import {
  IconType,
  IoniconsFilled,
  IoniconsOutline,
  NavigationData,
  PtUiAppRoutes,
  REQUIRED_VALIDATOR_KEY,
} from '@pt/pt-models';
import { DuplicateTreatmentPayload } from '../../../models';
import {
  Folder,
  LegalEntity,
  TreatmentCategory,
} from '@ms-organization-entities';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { ListDepartmentForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/department/ListDepartmentForMenuResponse';
import { ListUserForMenuResponse } from 'it/primatoo/openapi/api/org/schemas/user/ListUserForMenuResponse';
import { Department } from 'it/primatoo/openapi/api/org/schemas/department/Department';
import { User } from 'it/primatoo/openapi/api/org/schemas/user/listuserformenuresponse/User';
import { Item } from 'it/primatoo/openapi/api/org/schemas/treatment/_duplicateanalysisresponse/Item';
import { TreatmentDocCreateRequest } from '@primatoo-ms-organization';

@Component({
  selector: 'pt-duplicate-treatment-holder-to-responsible-dialog',
  styleUrls: [
    'duplicate-treatment-holder-to-responsible-dialog.component.scss',
  ],
  template: `
    <div class="modal-header" (click)="$event.stopPropagation()">
      <div class="d-flex flex-column">
        <h4 class="modal-title">Duplica da Titolare a Responsabile</h4>
        <span
          class="text-muted fst-italic small"
          *ngIf="duplicateTreatmentPayload?.payload?.name"
        >
          {{ duplicateTreatmentPayload?.payload?.name }}
        </span>
      </div>
    </div>

    <div
      class="modal-body modal-body--duplicate"
      (click)="$event.stopPropagation()"
    >
      <div class="dup-stepper">
        <!-- Indicatore step -->
        <ul class="dup-stepper__nav">
          <li
            class="dup-stepper__step"
            [class.is-active]="current === 0"
            [class.is-done]="current > 0"
            (click)="goTo(0)"
          >
            <span class="dup-stepper__badge">
              <pt-icon *ngIf="current > 0" [key]="faCheck"></pt-icon>
              <span *ngIf="current === 0">1</span>
            </span>
            <span class="dup-stepper__label">Verifica duplicazione</span>
          </li>

          <li class="dup-stepper__line" [class.is-done]="current > 0"></li>

          <li
            class="dup-stepper__step"
            [class.is-active]="current === 1"
            (click)="goTo(1)"
          >
            <span class="dup-stepper__badge"><span>2</span></span>
            <span class="dup-stepper__label">Compilazione</span>
          </li>
        </ul>

        <!-- Contenuto step -->
        <div class="dup-stepper__content card border-0 shadow-sm">
          <ng-container *ngIf="loading">
            <div class="loader-container">
              <pt-loader></pt-loader>
            </div>
          </ng-container>

          <ng-container *ngIf="!loading">
            <div class="card-body p-0" [ngSwitch]="current">
              <!-- Step 1: visualizzazione / controllo -->
              <pt-duplication-analysis
                *ngSwitchCase="0"
                [skippedItems]="skippedItems()"
              >
              </pt-duplication-analysis>

              <!-- Step 2: compilazione (ancora da fare) -->
              <div *ngSwitchCase="1" class="p-4 text-muted">
                <ng-container *ngIf="formGroup && !loading && !createFailed">
                  <form [formGroup]="formGroup">
                    <div class="form-group" *ngIf="checkUsedField('name')">
                      <label class="form-label"
                        >{{
                          'LABELS.NAME_FOR_TREATMENT_' + treatmentType
                            | translate
                        }}
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        autocomplete="off"
                        formControlName="name"
                        [maxLength]="128"
                        class="form-control form-control-sm"
                      />
                    </div>

                    <!--INTERNAL ID----------------------------------------------------------------->
                    <pt-input-text-conditional-checkbox
                      [label]="'LABELS.INTERNAL_ID' | translate"
                      [checkboxLabel]="'LABELS.DEFAULT_INTERNAL_ID' | translate"
                      [cleanOnDisable]="true"
                      [enableOnCheck]="false"
                      [defaultCheckValue]="true"
                      [controlName]="'internalId'"
                      [activeValidatorsOnlyWhenEnabledInput]="true"
                      formControlName="internalId"
                      [validators]="[REQUIRED]"
                      *ngIf="checkUsedField('internalId')"
                    ></pt-input-text-conditional-checkbox>

                    <div
                      class="form-group"
                      *ngIf="checkUsedField('description') && false"
                    >
                      <label class="form-label">{{
                        'LABELS.DESCRIPTION' | translate
                      }}</label>
                      <textarea
                        autocomplete="off"
                        formControlName="description"
                        class="form-control form-control-sm"
                        [rows]="5"
                        aria-describedby="ptWInputHelp"
                      >
                      </textarea>
                    </div>
                    <!--LEGAL ENTITY----------------------------------------------------------------->
                    <ng-template #popContentLegalEntityContent class="p-0">
                      <pt-resource-navigation
                        [title]="
                          'TITLE.SELECT_LEGAL_ENTITY_FOR_TREATMENT_' +
                            treatmentType | translate
                        "
                        [data]="legalEntity"
                        (clickClose)="popContentLegalEntity.close()"
                        [selectedResourceId]="selectedLegalEntity?.id"
                        (clickItem)="selectLegalEntity($event)"
                      >
                      </pt-resource-navigation>
                    </ng-template>

                    <div *ngIf="checkUsedField('legalEntityId')">
                      <label class="form-label"
                        >{{
                          'LABELS.SELECT_LEGAL_ENTITY_FOR_TREATMENT_' +
                            treatmentType | translate
                        }}
                        <span class="text-danger">*</span>
                      </label>
                      <div class="input-group input-group-sm">
                        <div class="input-group-text">
                          <pt-icon
                            [key]="'legal-entity.svg'"
                            [size]="14"
                            [iconType]="'SVG'"
                          ></pt-icon>
                        </div>
                        <input
                          type="text"
                          autocomplete="off"
                          formControlName="legalEntityId"
                          class="form-control form-control-sm form-readonly--custom"
                          aria-describedby="help"
                          [ngbPopover]="popContentLegalEntityContent"
                          [autoClose]="'outside'"
                          #popContentLegalEntity="ngbPopover"
                          triggers="manual"
                          readonly
                          placement="top"
                          (click)="chooseLegalEntity()"
                          placeholder="{{
                            'PLACEHOLDERS.SELECT_LEGAL_ENTITY_FOR_TREATMENT_' +
                              treatmentType | translate
                          }}"
                        />

                        <div class="mt-2">
                          <small class="form-text text-muted">
                            {{
                              'LABELS.DUPLICATE_RESPONSIBLE_INFO' | translate
                            }}
                          </small>
                        </div>

                        <button
                          [disabled]="loading"
                          type="button"
                          class="btn-close tree-selector-clean font-10"
                          aria-label="Close"
                          (click)="cleanLegalEntity(); $event.stopPropagation()"
                        >
                          <!--            <span aria-hidden='true'>&times;</span>-->
                        </button>
                      </div>
                    </div>

                    <!--TREATMENT CATEGORIES----------------------------------------------------------------->
                    <ng-template
                      #popContentTreatmentCategoryContent
                      class="p-0"
                    >
                      <pt-resource-navigation
                        [title]="'TITLE.SELECT_TREATMENT_CATEGORY' | translate"
                        [data]="treatmentCategories"
                        [selectedResourceId]="selectedTreatmentCategory?.id"
                        (clickClose)="popContentTreatmentCategory.close()"
                        (clickItem)="selectTreatmentCategory($event)"
                      >
                      </pt-resource-navigation>
                    </ng-template>

                    <div
                      class="tree-selector"
                      *ngIf="checkUsedField('treatmentCategoryId')"
                    >
                      <label class="form-label">{{
                        'LABELS.TREATMENT_CATEGORY' | translate
                      }}</label>
                      <div class="input-group input-group-sm mb-3">
                        <div class="input-group-text">
                          <pt-icon
                            [key]="'treatment.svg'"
                            [size]="14"
                            [iconType]="'SVG'"
                          ></pt-icon>
                        </div>
                        <input
                          type="text"
                          autocomplete="off"
                          formControlName="treatmentCategoryId"
                          class="form-control form-control-sm form-readonly--custom"
                          [ngbPopover]="popContentTreatmentCategoryContent"
                          [autoClose]="'outside'"
                          #popContentTreatmentCategory="ngbPopover"
                          triggers="manual"
                          readonly
                          placement="top"
                          (click)="chooseTreatmentCategory()"
                          placeholder="{{
                            'PLACEHOLDERS.TREATMENT_CATEGORY' | translate
                          }}"
                        />

                        <button
                          type="button"
                          [disabled]="loading"
                          class="btn-close tree-selector-clean font-10"
                          aria-label="Close"
                          (click)="
                            cleanTreatmentCategory(); $event.stopPropagation()
                          "
                        >
                          <!--            <span aria-hidden='true'>&times;</span>-->
                        </button>
                      </div>
                    </div>

                    <!--DEPARTMENT ----------------------------------------------------------------->
                    <ng-template #popContentDepartmentContent class="p-0">
                      <pt-resource-navigation
                        [title]="'TITLE.SELECT_DEPARTMENT' | translate"
                        [data]="departmentsNavData"
                        [selectedResourceId]="selectedDepartment?.id"
                        (clickClose)="popContentDepartment.close()"
                        (clickItem)="selectDepartment($event)"
                      >
                      </pt-resource-navigation>
                    </ng-template>

                    <div
                      class="tree-selector"
                      *ngIf="checkUsedField('department')"
                    >
                      <label class="form-label">{{
                        'LABELS.DEPARTMENT' | translate
                      }}</label>
                      <div class="input-group input-group-sm mb-3">
                        <div class="input-group-text">
                          <pt-icon
                            [key]="'treatment.svg'"
                            [size]="14"
                            [iconType]="'SVG'"
                          ></pt-icon>
                        </div>
                        <input
                          type="text"
                          autocomplete="off"
                          formControlName="departmentId"
                          class="form-control form-control-sm form-readonly--custom"
                          [ngbPopover]="popContentDepartmentContent"
                          [autoClose]="'outside'"
                          #popContentDepartment="ngbPopover"
                          triggers="manual"
                          placement="top"
                          readonly
                          (click)="chooseDepartment()"
                          placeholder="{{
                            'PLACEHOLDERS.DEPARTMENT' | translate
                          }}"
                        />

                        <button
                          type="button"
                          [disabled]="loading"
                          class="btn-close tree-selector-clean font-10"
                          aria-label="Close"
                          (click)="cleanDepartment(); $event.stopPropagation()"
                        >
                          <!--            <span aria-hidden='true'>&times;</span>-->
                        </button>
                      </div>
                    </div>

                    <!--departmentProcessOwner ----------------------------------------------------------------->
                    <ng-template #popContentProcessOwnerContent class="p-0">
                      <pt-resource-navigation
                        [title]="'TITLE.SELECT_PROCESS_OWNER' | translate"
                        [data]="departmentProcessOwner"
                        [selectedResourceId]="
                          selectedDepartmentProcessOwner?.id
                        "
                        (clickClose)="popContentProcessOwner.close()"
                        (clickItem)="selectProcessOwner($event)"
                      >
                      </pt-resource-navigation>
                    </ng-template>

                    <div
                      class="tree-selector"
                      *ngIf="checkUsedField('processOwner')"
                    >
                      <label class="form-label">
                        {{ 'LABELS.PROCESS_OWNER' | translate }}

                        <pt-icon
                          [key]="IconInfoCircle"
                          [size]="16"
                          class="d-inline-flex"
                          ngbTooltip="{{
                            'TOOLTIP.PROCESS_OWNER_CREATE_DOCUMENT' | translate
                          }}"
                          placement="bottom"
                          container="body"
                        ></pt-icon>
                      </label>
                      <div class="input-group input-group-sm mb-3">
                        <div class="input-group-text">
                          <pt-icon
                            [key]="'user.svg'"
                            [size]="14"
                            [iconType]="'SVG'"
                          ></pt-icon>
                        </div>
                        <input
                          type="text"
                          autocomplete="off"
                          formControlName="processOwnerId"
                          class="form-control form-control-sm form-readonly--custom"
                          [ngbPopover]="popContentProcessOwnerContent"
                          [autoClose]="'outside'"
                          #popContentProcessOwner="ngbPopover"
                          triggers="manual"
                          readonly
                          placement="top"
                          (click)="chooseProcessOwner()"
                          placeholder="{{
                            'PLACEHOLDERS.PROCESS_OWNER' | translate
                          }}"
                        />

                        <button
                          type="button"
                          [disabled]="loading"
                          class="btn-close tree-selector-clean font-10"
                          aria-label="Close"
                          (click)="
                            cleanProcessOwner(); $event.stopPropagation()
                          "
                        ></button>
                      </div>
                    </div>
                  </form>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

      <ng-container *ngIf="createFailed">
        <div
          style="
              text-align: center;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            "
        >
          <p>Il documento che si vuole creare non è stato configurato.</p>
          <p>
            {{ errorMessage }}
          </p>
          <p>Contattare il supporto</p>
          <a
            class="btn btn-sm btn-link mt-3 d-flex align-items-end"
            href="mailto:{{
              email
            }}?subject=[Primatoo] Request information about Management Workspace module"
          >
            <pt-icon
              [iconType]="IconType.IONIC_ICON"
              [key]="IconOpen"
              [size]="16"
            ></pt-icon>
            {{ 'BUTTON.CONTACT' | translate }}
          </a>
        </div>
      </ng-container>
    </div>

    <div class="modal-footer" (click)="$event.stopPropagation()">
      <button
        type="button"
        *ngIf="current === 0"
        class="btn btn-primary btn-sm px-3"
        (click)="next(); $event.stopPropagation()"
      >
        Procedi
      </button>

      <button
        type="button"
        *ngIf="current === 1"
        class="btn btn-primary btn-sm"
        [disabled]="formGroup?.invalid || loading || createFailed"
        (click)="confirm(); $event.stopPropagation()"
      >
        Conferma
      </button>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        [disabled]="loading"
        (click)="activeModal.close(false); $event.stopPropagation()"
      >
        Cancella
      </button>
    </div>
  `,
})
export class DuplicateTreatmentHolderToResponsibleDialogComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() openOnSuccess = true;
  treatmentType: TreatmentType = TreatmentType.RESPONSIBLE;

  @Input() duplicateTreatmentPayload: DuplicateTreatmentPayload;
  @Input() docId: number;
  @Input() selectedFolderId: number;
  @Input() selectedLegalEntityId: number;
  @Input() selectedTreatmentCategoryId: number;
  @Input() selectedDepartmentId: number;

  @ViewChild('popContentLegalEntity') popContentLegalEntity: NgbPopover;
  @ViewChild('popContentTreatmentCategory')
  popContentTreatmentCategory: NgbPopover;
  @ViewChild('popContentDepartment') popContentDepartment: NgbPopover;
  @ViewChild('popContentProcessOwner') popContentProcessOwner: NgbPopover;

  public readonly faCheck = IoniconsFilled.check;

  /** 0 = verifica (visualizzazione), 1 = compilazione */
  current = 0;

  goTo(step: number): void {
    this.current = step;
  }

  next(): void {
    if (this.current < 1) {
      this.current++;
    }
  }

  skippedItems: WritableSignal<Array<Item>> = signal<Array<Item>>([]);
  IconInfoCircle = IoniconsOutline.infoCircle;

  public org$: Observable<Organization> = this.store.select(
    PtCoreSelectors.organization
  );
  public editInternalIdEnable = false;

  public TreatmentType = TreatmentType;
  public folders: NavigationData[] = [];
  public legalEntity: NavigationData[] = [];
  public treatmentCategories: NavigationData[] = [];
  public departmentsNavData: NavigationData[] = [];
  public departmentProcessOwner: NavigationData[] = [];
  public departments: Department[] = [];

  public selectedFolder: NavigationData;
  public selectedLegalEntity: NavigationData;
  public selectedTreatmentCategory: NavigationData;
  public selectedDepartment: NavigationData;
  public selectedDepartmentProcessOwner: NavigationData;

  public loading = false;
  public createFailed = false;
  public formGroup: FormGroup;
  public REQUIRED = ValidatorGenerator.generateWidgetValidator(
    REQUIRED_VALIDATOR_KEY
  );

  private fields: string[] = [];

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private users: Array<User> = [];
  public errorMessage?: string;

  IconOpen = IoniconsOutline.mail;
  email?: string;

  constructor(
    public store: Store,
    public activeModal: NgbActiveModal,
    public actionsSubject: ActionsSubject,
    private folderService: FolderService,
    private departmentService: DepartmentService,
    private treatmentService: TreatmentService,
    private legalEntityService: LegalEntityService,
    private treatmentCategoryService: TreatmentCategoryService,
    private userService: UsersService,
    private systemService: SystemService
  ) {
    this.systemService.getSupportInfo().subscribe(
      (success) => {
        this.email = success.email;
      },
      () => {
        this.email = undefined;
      }
    );
  }

  checkUsedField(field: string): boolean {
    return this.fields?.includes(field);
  }

  ngAfterViewInit(): void {
    this.initNavigationData();
    const docId = this.docId;
    if (docId) {
      this.loading = true;
      this.treatmentService
        .duplicateHolderToResponsibleAnalysis(docId)
        .subscribe(
          (response) => {
            this.skippedItems.set(response.skippedItems);
            this.loading = false;
          },
          () => {
            this.loading = false;
          }
        );
    } else {
      console.warn('Missing DocID');
    }
  }

  setLoading(value: boolean, withDelay = false) {
    if (!withDelay) {
      setTimeout(() => {
        this.loading = value;
      });
    } else {
      setTimeout(() => {
        this.loading = value;
      }, 300);
    }
  }

  ngOnInit(): void {
    this.org$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
      this.editInternalIdEnable = org.editInternalIdEnable;
      this.alignDisabled();
    });

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.createTreatmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (action: {
          id: number;
          treatmentType:
            | 'HOLDER'
            | 'RESPONSIBLE'
            | 'PROJECT'
            | 'SUB_RESPONSIBLE';
        }) => {
          if (this.openOnSuccess) {
            this.openTreatment(action?.id);
          }
          this.activeModal.close(true);
        }
      );

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.createTreatmentFailed),
        takeUntil(this.destroy$)
      )
      .subscribe((action: { error: string }) => {
        this.loading = false;
        this.errorMessage = action.error;
        this.createFailed = true;
      });

    this.actionsSubject
      .pipe(
        ofType(TreatmentActions.duplicateTreatmentSuccess),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (this.openOnSuccess) {
          this.openTreatment(action?.response?.id);
        }
        this.activeModal.close(true);
      });
  }

  private openTreatment(id: number) {
    this.store.dispatch(
      PtCoreActions.navigateTo({
        route: [
          PtUiAppRoutes.BUSINESS_MANAGEMENT,
          PtUiAppRoutes.TREATMENTS_RESPONSIBLE,
          PtUiAppRoutes.EDIT,
          id.toString(),
        ],
      })
    );
  }

  private alignDisabled() {
    if (this.formGroup) {
      if (this.editInternalIdEnable) {
        this.formGroup.controls.internalId.enable();
      } else {
        this.formGroup.controls.internalId.disable();
      }
    }
  }

  private initNavigationData() {
    this.setLoading(true);
    forkJoin({
      department: this.departmentService.findAllForMenu(),
      folders: this.folderService.findAllFolders(),
      legalEntity: this.legalEntityService.getInternalLegalEntities(),
      treatmentCategories:
        this.treatmentCategoryService.getTreatmentCategories(),
      users: this.userService.getMenu(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: {
          department: ListDepartmentForMenuResponse;
          folders: Folder[];
          legalEntity: LegalEntity[];
          treatmentCategories: TreatmentCategory[];
          users: ListUserForMenuResponse;
        }) => {
          if (response.folders) {
            this.folders = response.folders.map((f) =>
              NavigationData.folderToNavigationData(f)
            );
          }
          if (response.legalEntity) {
            this.legalEntity = response.legalEntity
              .filter((l) => !l.deleted)
              .map((l) => NavigationData.legalEntityToNavigationData(l));
          }
          if (response.treatmentCategories) {
            this.treatmentCategories = response.treatmentCategories
              .filter((t) => !t.deleted)
              .map((t) => NavigationData.treatmentCategoryToNavigationData(t));
          }
          if (response.department) {
            this.departments = response.department.departments;
            this.departmentsNavData = this.departments.map((t) =>
              NavigationData.departmentToNavigationData(t)
            );
          }
          if (response.users) {
            this.users = response.users.users;
            this.departmentProcessOwner = this.users.map((t) =>
              NavigationData.userToNavigationData(t)
            );
          }
          this.initFormGroup();
        }
      );
  }

  private initFormGroup() {
    this.selectedFolder = this.folders?.find(
      (f) => f.id == this.duplicateTreatmentPayload?.payload?.parentId
    );
    this.selectedLegalEntity = this.legalEntity?.find(
      (f) => f.id == this.duplicateTreatmentPayload?.payload?.legalEntityId
    );
    this.selectedTreatmentCategory = this.treatmentCategories?.find(
      (f) =>
        f.id == this.duplicateTreatmentPayload?.payload?.treatmentCategoryId
    );
    this.selectedDepartment = this.departmentsNavData?.find(
      (f) => f.id == this.duplicateTreatmentPayload?.payload?.departmentId
    );
    this.selectedDepartmentProcessOwner = this.departmentProcessOwner?.find(
      (f) => f.id == this.duplicateTreatmentPayload?.payload?.processOwnerId
    );

    this.treatmentService
      .getToOverride(TreatmentType.RESPONSIBLE)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.fields = Object.keys(response).filter((r) => response[r] === true);

        this.formGroup = new FormGroup({
          name: new FormControl(null, [
            Validators.required,
            Validators.maxLength(128),
          ]),
          internalId: new FormControl(null),
          description: new FormControl(
            this.duplicateTreatmentPayload?.payload?.description
          ),
          treatmentCategoryId: new FormControl(
            this.selectedTreatmentCategory?.name
          ),
          departmentId: new FormControl(this.selectedDepartment?.name),
          processOwnerId: new FormControl(
            this.selectedDepartmentProcessOwner?.name
          ),
          legalEntityId: new FormControl(this.selectedLegalEntity?.name, [
            Validators.required,
          ]),
        });
        this.alignDisabled();
      });
    this.setLoading(false, true);
  }

  confirm() {
    this.setLoading(true);

    const request = {
      name: this.formGroup.controls.name.value,
      description: this.formGroup.controls.description.value,
      internalId: this.formGroup.controls.internalId.value,
      legalEntityId: this.selectedLegalEntity?.id,
      parentId: this.selectedFolder ? this.selectedFolder?.id : null,
      treatmentCategoryId: this.selectedTreatmentCategory?.id,
      departmentId: this.selectedDepartment?.id,
      processOwnerId: this.selectedDepartmentProcessOwner?.id,
      treatmentType: TreatmentType.RESPONSIBLE,
    } as TreatmentDocCreateRequest;

    this.store.dispatch(
      TreatmentActions.duplicateTreatmentHolderToResponsible({
        id: this.duplicateTreatmentPayload.payload?.docId,
        request: request,
      })
    );
  }

  selectLegalEntity(resource: NavigationData) {
    this.popContentLegalEntity.close();
    this.selectedLegalEntity = resource;
    this.formGroup.controls.legalEntityId.setValue(
      this.selectedLegalEntity?.name
    );
  }
  //
  selectTreatmentCategory(resource: NavigationData) {
    this.popContentTreatmentCategory.close();
    this.selectedTreatmentCategory = resource;
    this.formGroup.controls.treatmentCategoryId.setValue(
      this.selectedTreatmentCategory?.name
    );
  }

  selectDepartment(resource: NavigationData) {
    this.popContentDepartment.close();
    this.selectedDepartment = resource;
    this.formGroup.controls.departmentId.setValue(
      this.selectedDepartment?.name
    );
    const dep = this.departments.find(
      (d) => d.id === this.selectedDepartment?.id
    );
    if (dep && dep.processOwnerId) {
      this.selectedDepartmentProcessOwner = this.departmentProcessOwner.find(
        (d) => d.id === dep.processOwnerId
      );
      this.formGroup.controls.processOwnerId.setValue(
        this.selectedDepartmentProcessOwner?.name
      );
    }
  }

  selectProcessOwner(resource: NavigationData) {
    this.popContentDepartment.close();
    this.selectedDepartmentProcessOwner = resource;
    this.formGroup.controls.processOwnerId.setValue(
      this.selectedDepartmentProcessOwner?.name
    );
  }

  chooseLegalEntity() {
    this.popContentLegalEntity.open();
  }

  chooseTreatmentCategory() {
    this.popContentTreatmentCategory.open();
  }
  //
  chooseProcessOwner() {
    this.popContentProcessOwner.open();
  }
  //
  chooseDepartment() {
    this.popContentDepartment.open();
  }
  //
  cleanLegalEntity() {
    this.formGroup?.controls?.legalEntityId?.setValue(null);
    this.selectedLegalEntity = null;
  }

  cleanTreatmentCategory() {
    this.formGroup?.controls?.treatmentCategoryId?.setValue(null);
    this.selectedTreatmentCategory = null;
  }

  cleanDepartment() {
    this.formGroup?.controls?.departmentId?.setValue(null);
    this.selectedDepartment = null;
  }

  cleanProcessOwner() {
    this.formGroup?.controls?.processOwnerId?.setValue(null);
    this.selectedDepartmentProcessOwner = null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  protected readonly IconType = IconType;
}
